import { AuthPageLayout } from "./AuthPageLayout";
import { LoadingButton } from "./LoadingButton";

/**
 * パスワードリセット開始画面（メールアドレスまたは ID の入力）
 *
 * PRODUCT_DEV-891: 独自ID（例「行員番号」）でも受ける。メールアドレスの無い利用者には
 * 送信後に「管理者に再発行を依頼して」と案内する（テナント専用の画面は作らない）。
 */
export const ForgotPasswordUI: React.FC<{
  loading: boolean;
  error: string | null;
  email: string;
  /** 入力欄の呼び方。`buildResetIdLabel` の戻り値（例「メールアドレスまたは行員番号」）。 */
  idLabel: string;
  onEmailChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  /**
   * PRODUCT_DEV-1238: 会社で「管理者に発行を依頼する」が使えるとき、案内文を変え、
   * 「管理者からコードを受け取った方」への入口を出す。
   */
  reissueEnabled?: boolean;
  onOpenReissueCode?: () => void;
}> = ({
  loading,
  error,
  email,
  idLabel,
  onEmailChange,
  onSubmit,
  onCancel,
  reissueEnabled = false,
  onOpenReissueCode,
}) => {
  return (
    <AuthPageLayout>
      {/* タイトル */}
      <h2 className="text-3xl font-bold text-center text-neutral-900 mb-4">
        パスワードをリセット
      </h2>
      <p className="text-center text-neutral-600 mb-8">
        {idLabel}を入力してください。
        {/* PRODUCT_DEV-1238: 依頼が使える会社は一文だけ（メールで戻せない人もこの画面から進むため） */}
        {!reissueEnabled && (
          <>
            <br />
            登録されているメールアドレスに、パスワードリセット用のコードを送信します。
          </>
        )}
      </p>

      {/* フォーム */}
      <form onSubmit={onSubmit} className="space-y-6">
        {/* メールアドレスまたは ID（独自ID が通るよう type="email" にしない） */}
        <div>
          <input
            id="forgot-email"
            type="text"
            autoComplete="username"
            // 375px で「〜を入力してください」まで入れると欄から溢れて切れる（案内は上の文に書いてある）
            placeholder={idLabel}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full px-6 py-4 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-white"
            disabled={loading}
          />
        </div>

        {/* エラーメッセージ */}
        {error && <div className="text-sm text-red-600">{error}</div>}

        {/* 案内メッセージ（依頼が使える会社では出さない。2026-10-26 UI 確認） */}
        {!reissueEnabled && (
          <div className="text-sm text-neutral-600 bg-neutral-50 p-4 rounded-lg">
            メールアドレスを登録していない場合や、メールが届かない場合は、管理者にパスワードの再発行を依頼してください。
          </div>
        )}

        {/* ボタン */}
        <div className="space-y-3">
          <LoadingButton
            loading={loading}
            loadingMessage="送信中..."
            label={reissueEnabled ? "パスワードを再発行する" : "リセットコードを送信"}
          />

          <button
            type="button"
            onClick={onCancel}
            className="w-full bg-gray-500 hover:bg-gray-600 text-white py-4 rounded-2xl text-xl font-bold transition-colors"
            disabled={loading}
          >
            ログインに戻る
          </button>
        </div>
        {reissueEnabled && onOpenReissueCode && (
          <button
            type="button"
            onClick={onOpenReissueCode}
            className="w-full text-center text-sm font-bold text-primary py-2"
            disabled={loading}
          >
            管理者からコードを受け取った方
          </button>
        )}
      </form>
    </AuthPageLayout>
  );
};
